import React, { useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { api, fmtError } from "@/lib/api";
import RatingStars from "@/components/RatingStars";

/**
 * Modal de saisie d'un avis post-mission.
 * - Étoiles 1-5 obligatoires (aucune sélection => bouton désactivé)
 * - Commentaire libre 5-2000 caractères, compteur en temps réel
 * - `onSubmitted(avis)` : callback si succès (parent recharge la liste)
 * - `onClose` : fermeture manuelle
 * - `mission` : objet mission (titre, contrepartie utilisés dans l'entête)
 */
const MAX = 2000;
const MIN = 5;

export default function RatingModal({ mission, contrepartie, onClose, onSubmitted }) {
  const [note, setNote] = useState(0);
  const [commentaire, setCommentaire] = useState("");
  const [busy, setBusy] = useState(false);

  const canSubmit = note >= 1 && commentaire.trim().length >= MIN && commentaire.length <= MAX && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const { data } = await api.post("/avis", {
        mission_id: mission.id,
        note,
        commentaire: commentaire.trim(),
      });
      toast.success("Avis envoyé. Merci pour votre retour.");
      onSubmitted?.(data);
      onClose();
    } catch (err) {
      toast.error(fmtError(err));
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rating-modal-title"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-md shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 py-4 border-b border-[#E4E7EB]">
          <div>
            <h2 id="rating-modal-title" className="text-lg font-semibold text-[#1F2933]">Noter cette mission</h2>
            <p className="text-xs text-[#3E4C59] mt-0.5">
              <span className="font-medium">{mission.titre}</span>
              {contrepartie && <span> · avec {contrepartie}</span>}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Fermer"
            data-testid="rating-modal-close"
            className="text-[#3E4C59] hover:text-[#1F2933]"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="px-5 py-5 space-y-5">
          <div>
            <label className="block text-sm font-medium text-[#1F2933] mb-2">
              Votre note <span className="text-[#C0392B]">*</span>
            </label>
            <RatingStars
              value={note}
              onChange={setNote}
              size={32}
              data-testid="rating-modal-star"
            />
            <p className="text-xs text-[#3E4C59] mt-1">
              {note === 0 ? "Cliquez pour attribuer une note de 1 à 5." : `${note} / 5`}
            </p>
          </div>

          <div>
            <label htmlFor="rating-comment" className="block text-sm font-medium text-[#1F2933] mb-2">
              Commentaire <span className="text-[#C0392B]">*</span>
              <span className="text-xs text-[#3E4C59] font-normal ml-2">
                (min {MIN}, max {MAX} caractères — visible publiquement)
              </span>
            </label>
            <textarea
              id="rating-comment"
              rows={5}
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value.slice(0, MAX))}
              placeholder="Qualité du travail, respect des délais, communication, points forts, ce qui pourrait être amélioré…"
              data-testid="rating-modal-comment"
              className="w-full border border-[#CBD5E1] rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A5C] resize-y"
            />
            <div className={`text-xs mt-1 ${commentaire.length < MIN ? "text-[#C0392B]" : "text-[#3E4C59]"}`}>
              {commentaire.length} / {MAX}
              {commentaire.length > 0 && commentaire.length < MIN && ` — encore ${MIN - commentaire.length} caractère(s) minimum`}
            </div>
          </div>

          <div className="bg-[#F7F8FA] border-l-4 border-[#1B3A5C] px-3 py-2 text-xs text-[#3E4C59] leading-relaxed">
            Votre avis est <strong>public</strong> et lié à votre nom. Le destinataire peut y ajouter une réponse.
            Un avis diffamatoire ou hors-sujet peut être masqué par la modération.
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#E4E7EB]">
            <button
              type="button"
              onClick={onClose}
              data-testid="rating-modal-cancel"
              className="btn-ghost"
            >Annuler</button>
            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="rating-modal-submit"
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? "Envoi…" : "Envoyer l'avis"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
